import { useState } from "react";
import { Maximize2, Minimize2, Send } from "lucide-react";
import { toast } from "sonner";
import api from "@/api/client";
import { ChevronDown, ExternalLink, Film, Pencil, MessageSquare, Flag, History as HistoryIcon, Clock, Link2, Info, Timer } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import Avatar from "@/components/Avatar";
import TaskTimer from "@/components/TaskTimer";
import { STATUS_META, STATUS_ORDER, PRIORITY_META, PRIORITY_ORDER, priorityOf } from "@/constants/status";
import { fmtDateTime } from "@/utils/date";

function Section({ id, icon: Icon, title, count, defaultOpen = true, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-white/10" data-testid={`task-panel-section-${id}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid={`task-panel-section-${id}-toggle`}
        aria-expanded={open}
        className="w-full flex items-center gap-2 px-5 py-3 text-sm font-semibold hover:bg-white/[0.03] transition-colors"
      >
        <Icon size={15} className="text-white/50" />
        <span className="flex-1 text-left">{title}</span>
        {count != null && <span className="text-xs text-white/50 font-normal">{count}</span>}
        <ChevronDown size={15} className={`text-white/50 transition-transform ${open ? "" : "-rotate-90"}`} />
      </button>
      {open && <div className="px-5 pb-4">{children}</div>}
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="grid grid-cols-[110px_1fr] items-center gap-3 py-1.5 text-sm">
      <div className="text-white/50">{label}</div>
      <div className="min-w-0">{children}</div>
    </div>
  );
}

export default function TaskSidePanel({
  open, onOpenChange, task, status, assignee, currentUser, isManager, canEdit,
  onEdit, onStatusChange, rawOnStatusChange, onTogglePriority, onOpenComments, onCommentAdded,
}) {
  const prio = priorityOf(task);
  const [wide, setWide] = useState(() => localStorage.getItem("lim-task-panel-wide") === "1");
  const toggleWide = () => setWide((w) => { localStorage.setItem("lim-task-panel-wide", w ? "0" : "1"); return !w; });
  const [localComments, setLocalComments] = useState(null);
  const comments = localComments || task.comments || [];
  const [draft, setDraft] = useState("");
  const [posting, setPosting] = useState(false);
  const postComment = async (e) => {
    e.preventDefault();
    if (!draft.trim()) return;
    setPosting(true);
    try {
      const { data } = await api.post(`/tasks/${task.id}/comments`, { text: draft.trim() });
      setLocalComments(data.comments || []);
      setDraft("");
      toast.success("Comment posted");
      onCommentAdded?.(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to post comment");
    } finally { setPosting(false); }
  };
  const history = (task.history || []).slice().reverse();
  const names = (task.assignee_details || []).map((a) => a.name).filter(Boolean);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className={`bg-surface border-white/10 text-white w-full p-0 overflow-y-auto scrollbar-thin transition-[max-width] ${wide ? "sm:max-w-[min(1100px,92vw)]" : "sm:max-w-lg"}`} data-testid={`task-panel-${task.id}`}>
        <SheetHeader className="px-5 pt-5 pb-4 border-b border-white/10 text-left space-y-1.5">
          {(task.client || task.project_name) && (
            <div className="text-sm"><span className="client-line">{task.client}</span>{task.project_name && <span className="text-white/50"> · {task.project_name}</span>}</div>
          )}
          <SheetTitle className="font-display text-xl text-white pr-6 leading-snug">{task.title}</SheetTitle>
          <SheetDescription className="text-white/50 text-xs">Assigned by {task.assigned_by_name || "—"}</SheetDescription>
          <div className="flex items-center gap-2 pt-2">
            <button type="button" onClick={toggleWide} data-testid={`task-panel-expand-${task.id}`}
              title={wide ? "Shrink to side panel" : "Expand to wide view"} aria-label={wide ? "Shrink panel" : "Expand panel"}
              className="hidden sm:grid h-8 w-8 place-items-center rounded-md border border-white/10 text-white/60 hover:text-white hover:bg-white/5 order-last ml-auto">
              {wide ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
            </button>
            {onEdit && canEdit && (
              <Button size="sm" onClick={() => { onOpenChange(false); onEdit(task); }} data-testid={`task-panel-edit-${task.id}`}
                className="h-8 bg-[#E11D2E] hover:bg-[#F03041] text-white">
                <Pencil size={13} className="mr-1.5" /> Edit task
              </Button>
            )}
            {onOpenComments && <Button size="sm" variant="outline" onClick={onOpenComments} disabled={!!task._from_todo}
              data-testid={`task-panel-comment-${task.id}`} className="h-8 border-white/15 bg-transparent text-white hover:bg-white/5">
              <MessageSquare size={13} className="mr-1.5" /> All comments
            </Button>}
          </div>
        </SheetHeader>

        <div className={wide ? "sm:grid sm:grid-cols-[1.4fr_1fr] sm:items-start" : ""} data-testid="task-panel-body">
        <div>
        <Section id="details" icon={Info} title="Details">
          <Row label="Status">
            {onStatusChange && canEdit ? (
              <select
                value={status}
                onChange={(e) => onStatusChange(e.target.value)}
                data-testid={`task-panel-status-${task.id}`}
                className={`h-8 rounded-full px-3 text-xs font-medium ${STATUS_META[status]?.pill || ""} bg-transparent`}
              >
                {STATUS_ORDER.map((k) => <option key={k} value={k}>{STATUS_META[k].label}</option>)}
              </select>
            ) : (
              <span className={`text-xs px-2.5 py-1 rounded-full ${STATUS_META[status]?.pill || ""}`}>{STATUS_META[status]?.label}</span>
            )}
          </Row>
          <Row label="Priority">
            {isManager && onTogglePriority ? (
              <div className="flex flex-wrap gap-1" data-testid={`task-panel-priority-${task.id}`}>
                {PRIORITY_ORDER.map((k) => (
                  <button key={k} type="button" aria-pressed={prio === k} onClick={() => onTogglePriority(task, k)}
                    data-testid={`task-panel-priority-${task.id}-${k}`}
                    className={`h-7 px-2 rounded-md border text-xs inline-flex items-center gap-1 transition-colors ${prio === k ? "border-[#E11D2E]/50 bg-[#E11D2E]/10 text-white" : "border-white/10 text-white/60 hover:bg-white/5"}`}>
                    <Flag size={11} fill="currentColor" className={PRIORITY_META[k].text} /> {PRIORITY_META[k].label}
                  </button>
                ))}
              </div>
            ) : (
              <span className="inline-flex items-center gap-1.5 text-sm"><Flag size={12} fill="currentColor" className={PRIORITY_META[prio].text} /> {PRIORITY_META[prio].label}</span>
            )}
          </Row>
          <Row label="Assignee">
            <div className="flex items-center gap-2"><Avatar user={assignee} size={20} /><span className="truncate">{names.length > 1 ? names.join(", ") : (assignee?.name || task.assigned_to_name)}</span></div>
          </Row>
          <Row label="Start">{task.start_date ? fmtDateTime(task.start_date).split(" ")[0] : "—"}</Row>
          <Row label="Due"><span className="inline-flex items-center gap-1.5"><Clock size={12} className="text-white/50" />{task.deadline ? fmtDateTime(task.deadline) : "Up next (no date)"}</span></Row>
          {task.description && (
            <div className="mt-3 text-sm text-white/80 whitespace-pre-wrap leading-relaxed rounded-md bg-white/[0.03] border border-white/10 p-3">{task.description}</div>
          )}
          {task.hold_reason && <div className="mt-2 text-xs text-orange-300">On hold: {task.hold_reason}</div>}
          {task.incomplete_reason && <div className="mt-2 text-xs text-red-300">Incomplete: {task.incomplete_reason}</div>}
        </Section>

        {!task._from_todo && (
          <Section id="timer" icon={Timer} title="Time tracking">
            <TaskTimer task={task} currentUser={currentUser} isManager={isManager} onStatusChange={rawOnStatusChange} />
          </Section>
        )}

        <Section id="links" icon={Link2} title="Links" count={[task.nas_link, task.final_video_link].filter(Boolean).length}>
          {!task.nas_link && !task.final_video_link && <div className="text-sm text-white/50">No links added yet.</div>}
          {task.nas_link && (
            <a href={task.nas_link} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm text-blue-500 hover:underline py-1 break-all">
              <ExternalLink size={13} /> NAS workfile
            </a>
          )}
          {task.final_video_link && (
            <a href={task.final_video_link} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm text-emerald-500 hover:underline py-1 break-all">
              <Film size={13} /> Final video
            </a>
          )}
        </Section>

        </div>
        <div className={wide ? "sm:border-l sm:border-white/10 sm:min-h-full" : ""} data-testid="task-panel-activity">
        <Section id="comments" icon={MessageSquare} title="Comments" count={comments.length}>
          {!task._from_todo && (
            <form onSubmit={postComment} className="flex items-end gap-2 mb-3">
              <textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={2} placeholder="Write a comment…"
                data-testid={`task-panel-comment-input-${task.id}`}
                onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) postComment(e); }}
                className="flex-1 resize-none rounded-md border border-white/15 bg-transparent px-3 py-2 text-sm placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-[#E11D2E]/30" />
              <Button type="submit" size="sm" disabled={posting || !draft.trim()} data-testid={`task-panel-comment-send-${task.id}`}
                title="Post comment (Ctrl+Enter)" className="h-9 bg-[#E11D2E] hover:bg-[#F03041] text-white"><Send size={14} /></Button>
            </form>
          )}
          {comments.length === 0 ? (
            <div className="text-sm text-white/50">No comments yet.</div>
          ) : (
            <div className="space-y-2">
              {comments.slice(-5).map((c, i) => (
                <div key={i} className="rounded-md bg-white/[0.03] border border-white/10 p-2.5">
                  <div className="flex justify-between text-xs text-white/50 mb-1"><span className="font-medium">{c.by_name || c.user_name}</span><span>{fmtDateTime(c.at || c.created_at)}</span></div>
                  <div className="text-sm whitespace-pre-wrap">{c.text}</div>
                </div>
              ))}
            </div>
          )}
        </Section>

        <Section id="history" icon={HistoryIcon} title="History" count={history.length} defaultOpen={false}>
          {history.length === 0 ? <div className="text-sm text-white/50">No changes yet.</div> : (
            <ol className="space-y-2">
              {history.map((h, i) => (
                <li key={i} className="text-xs border-l-2 border-white/15 pl-3">
                  <div className="text-white/50">{h.by_name} · {fmtDateTime(h.at)}</div>
                  {Object.entries(h.previous || {}).map(([k, v]) => (
                    <div key={k} className="text-white/80">Previous {k}: {String(v) || "—"}</div>
                  ))}
                </li>
              ))}
            </ol>
          )}
        </Section>
        </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
